import { useMemo, useState } from "react";
import { AlertTriangle, Check, Plus, Trash2, Wrench, X } from "lucide-react";

import {
  useCapabilities,
  useConfirmProductCapability,
  useProductCapabilities,
  useRemoveProductCapability,
  useSetProductCapability,
} from "@/api/queries";
import type {
  Capability,
  CapabilityPropertySchema,
  Product,
  ProductCapability,
  ValueSource,
} from "@/api/types";
import { Button } from "@/components/Button";
import {
  ASSERTABLE_SOURCES,
  CERTAINTY_LABELS,
  parameterLabel,
  parameterValue,
  SOURCE_LABELS,
  unitOf,
} from "./capability-display";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { StatusBadge } from "@/components/StatusBadge";

/**
 * Ce que le produit sait faire (TICKET V1.5-06, ADR 0012).
 *
 * Trois choses que cette carte fait exprès :
 *
 * **Elle n'affiche jamais de JSON brut.** Les paramètres passent par le schéma
 * de la capacité, qui porte leur titre et leur unité. « max_diameter_mm: 38 »
 * s'écrit « Diamètre maximal : 38 mm ».
 *
 * **Elle distingue « confirmé » de « probable ».** Une capacité déduite et une
 * capacité validée par quelqu'un ne se lisent pas pareil, et c'est la première
 * chose qu'on regarde quand une réponse surprend.
 *
 * **Elle ne code en dur aucun formulaire.** Le formulaire de saisie est
 * engendré depuis `parameter_schema` : ajouter une capacité au vocabulaire ne
 * demande pas de toucher à cet écran.
 */

export function ProductCapabilitiesCard({ product }: { product: Product }) {
  const { data, isLoading } = useProductCapabilities(product.id);
  const [adding, setAdding] = useState(false);
  const rows = data?.items ?? [];

  return (
    <Card>
      <CardHeader
        title="Capacités"
        subtitle="Ce que ce produit permet de faire, et d'où HD-Vault le sait"
        action={
          <Button size="sm" variant="secondary" onClick={() => setAdding((open) => !open)}>
            {adding ? (
              <>
                <X className="size-4" aria-hidden />
                Annuler
              </>
            ) : (
              <>
                <Plus className="size-4" aria-hidden />
                Ajouter
              </>
            )}
          </Button>
        }
      />
      <CardBody className="flex flex-col gap-3">
        {adding && (
          <CapabilityForm
            productId={product.id}
            existing={rows.map((row) => row.capability_id)}
            onDone={() => setAdding(false)}
          />
        )}

        {isLoading && <p className="text-[12px] text-ink-subtle">Chargement…</p>}

        {!isLoading && rows.length === 0 && (
          <p className="text-[12px] text-ink-subtle">
            Aucune capacité enregistrée. Tant qu'il n'y en a pas, HD-Vault répond « je n'ai
            pas cette information » — jamais « ce produit ne le fait pas ».
          </p>
        )}

        {rows.length > 0 && (
          <ul className="flex flex-col divide-y divide-border">
            {rows.map((row) => (
              <CapabilityRow key={row.id} productId={product.id} row={row} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function CapabilityRow({ productId, row }: { productId: string; row: ProductCapability }) {
  const confirm = useConfirmProductCapability(productId);
  const remove = useRemoveProductCapability(productId);
  const certainty = CERTAINTY_LABELS[row.confidence];
  const properties = row.parameter_schema.properties ?? {};
  const entries = Object.entries(row.parameters);

  return (
    <li className="flex flex-col gap-1 py-2 first:pt-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="flex items-center gap-2 text-[14px] text-ink">
          <Wrench className="size-3.5 text-ink-subtle" aria-hidden />
          {row.label_fr}
          {row.state === "NOT_SUPPORTED" && (
            <StatusBadge tone="out">ne le fait pas</StatusBadge>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={certainty.tone}>{certainty.label}</StatusBadge>
          <span className="text-[11px] text-ink-subtle">
            Source : {SOURCE_LABELS[row.source]}
            {row.confirmed_by_human ? " · confirmé" : " · non confirmé"}
          </span>
        </span>
      </div>

      {entries.length > 0 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-0.5 pl-5">
          {entries.map(([name, value]) => (
            <li key={name} className="text-[12px] text-ink-subtle">
              {parameterLabel(name, properties[name])} : {parameterValue(name, value)}
            </li>
          ))}
        </ul>
      )}

      {row.source_reference && (
        <p className="pl-5 text-[11px] text-ink-subtle">Référence : {row.source_reference}</p>
      )}

      {!row.capability_active && (
        <p className="flex items-center gap-1 pl-5 text-[11px] text-warning">
          <AlertTriangle className="size-3" aria-hidden />
          Code déprécié : la ligne reste lisible, aucune nouvelle n'est acceptée.
        </p>
      )}

      <div className="flex gap-2 pl-5">
        {!row.confirmed_by_human && (
          <Button
            size="sm"
            variant="ghost"
            loading={confirm.isPending}
            onClick={() => confirm.mutate(row.id)}
          >
            <Check className="size-3.5" aria-hidden />
            Confirmer
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          loading={remove.isPending}
          onClick={() => remove.mutate(row.id)}
        >
          <Trash2 className="size-3.5" aria-hidden />
          Retirer
        </Button>
      </div>
    </li>
  );
}

/**
 * Le formulaire, engendré depuis le schéma de la capacité choisie.
 *
 * Aucun champ codé en dur par capacité : ajouter `MEASURE_PRESSURE` au
 * vocabulaire demain donnera son formulaire sans toucher à ce fichier. C'est
 * exactement pour ça que le schéma est stocké.
 */
function CapabilityForm({
  productId,
  existing,
  onDone,
}: {
  productId: string;
  existing: string[];
  onDone: () => void;
}) {
  const { data } = useCapabilities({ active: true });
  const save = useSetProductCapability(productId);
  const [capabilityId, setCapabilityId] = useState("");
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [state, setState] = useState<"SUPPORTED" | "NOT_SUPPORTED">("SUPPORTED");
  const [source, setSource] = useState<ValueSource>("HUMAN");
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);

  const available = useMemo(
    () => (data?.items ?? []).filter((item) => !existing.includes(item.id)),
    [data, existing],
  );
  const chosen: Capability | undefined = available.find((item) => item.id === capabilityId);
  const properties = chosen?.parameter_schema.properties ?? {};

  async function submit() {
    if (!capabilityId) return;
    setError(null);
    try {
      await save.mutateAsync({
        capability_id: capabilityId,
        parameters: values,
        state,
        source,
        source_reference: reference,
        confirm: true,
      });
      onDone();
    } catch (caught) {
      setError((caught as { message?: string })?.message ?? "Enregistrement refusé.");
    }
  }

  return (
    <form
      className="flex flex-col gap-3 rounded-md border border-border bg-surface-sunken p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <label className="flex flex-col gap-1 text-[12px] text-ink-subtle">
        Capacité
        <select
          value={capabilityId}
          onChange={(event) => {
            setCapabilityId(event.target.value);
            setValues({});
          }}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
        >
          <option value="">Choisir…</option>
          {available.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label_fr}
            </option>
          ))}
        </select>
      </label>

      {chosen?.description && (
        <p className="text-[11px] text-ink-subtle">{chosen.description}</p>
      )}

      {Object.entries(properties).map(([name, schema]) => (
        <ParameterField
          key={name}
          name={name}
          schema={schema}
          value={values[name]}
          onChange={(next) =>
            setValues((current) => {
              const updated = { ...current };
              if (next === undefined || next === "" || (Array.isArray(next) && !next.length)) {
                delete updated[name];
              } else {
                updated[name] = next;
              }
              return updated;
            })
          }
        />
      ))}

      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1 text-[12px] text-ink-subtle">
          Affirmation
          <select
            value={state}
            onChange={(event) =>
              setState(event.target.value as "SUPPORTED" | "NOT_SUPPORTED")
            }
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
          >
            <option value="SUPPORTED">Le produit le fait</option>
            <option value="NOT_SUPPORTED">Le produit ne le fait pas</option>
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[12px] text-ink-subtle">
          Source
          <select
            value={source}
            onChange={(event) => setSource(event.target.value as ValueSource)}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
          >
            {ASSERTABLE_SOURCES.map((item) => (
              <option key={item} value={item}>
                {SOURCE_LABELS[item]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex min-w-0 flex-1 flex-col gap-1 text-[12px] text-ink-subtle">
          Référence (facultatif)
          <input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder="fiche technique p. 4"
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
          />
        </label>
      </div>

      {error && (
        <p className="flex items-center gap-2 text-[12px] text-status-out">
          <AlertTriangle className="size-3.5" aria-hidden />
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" size="sm" loading={save.isPending} disabled={!capabilityId}>
          Enregistrer
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Annuler
        </Button>
      </div>
    </form>
  );
}

function ParameterField({
  name,
  schema,
  value,
  onChange,
}: {
  name: string;
  schema: CapabilityPropertySchema;
  value: unknown;
  onChange: (next: unknown) => void;
}) {
  const label = parameterLabel(name, schema);
  const unit = unitOf(name);
  const heading = unit ? `${label} (${unit})` : label;

  if (schema.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-[13px] text-ink">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(event) => onChange(event.target.checked ? true : undefined)}
        />
        {heading}
      </label>
    );
  }

  if (schema.type === "array") {
    const options = schema.items?.enum ?? [];
    const selected = Array.isArray(value) ? (value as string[]) : [];
    return (
      <fieldset className="flex flex-col gap-1">
        <legend className="text-[12px] text-ink-subtle">{heading}</legend>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {options.map((option) => (
            <label key={option} className="flex items-center gap-1 text-[13px] text-ink">
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selected, option]
                      : selected.filter((item) => item !== option),
                  )
                }
              />
              {option}
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  if (schema.enum) {
    return (
      <label className="flex flex-col gap-1 text-[12px] text-ink-subtle">
        {heading}
        <select
          value={value === undefined ? "" : String(value)}
          onChange={(event) => onChange(event.target.value || undefined)}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
        >
          <option value="">—</option>
          {schema.enum.map((option) => (
            <option key={String(option)} value={String(option)}>
              {String(option)}
            </option>
          ))}
        </select>
      </label>
    );
  }

  const numeric = schema.type === "number" || schema.type === "integer";
  return (
    <label className="flex flex-col gap-1 text-[12px] text-ink-subtle">
      {heading}
      <input
        type={numeric ? "number" : "text"}
        step={schema.type === "integer" ? 1 : "any"}
        min={schema.minimum}
        max={schema.maximum}
        value={value === undefined ? "" : String(value)}
        onChange={(event) => {
          const raw = event.target.value;
          if (raw === "") return onChange(undefined);
          onChange(numeric ? Number(raw) : raw);
        }}
        className="rounded-md border border-border bg-surface px-2 py-1.5 text-[14px] text-ink"
      />
    </label>
  );
}
